import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Languages } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { caretRect } from '@shared/nepali/browser/caret';
import { candidatesFor, rememberPick, worthOffering } from '@shared/nepali/browser/memory';
import {
  anchorIndex,
  backspaceAfterConversion,
  commitOnSpace,
  ConversionLog,
  stillValid,
  type Conversion,
} from '@shared/nepali/browser/typing';
import {
  alternatesAction,
  AlternatesPopup,
  isAlternatesTrigger,
  type AlternatesState,
} from '@/components/AlternatesPopup';

/**
 * A text field that converts romanized Nepali as you type.
 *
 * Same commit rule as the editor: the token converts when you press space, or
 * when the field loses focus. Converting per keystroke would fight the writer —
 * `k` would become क before they typed the vowel that makes it को.
 *
 * The preference is shared with the editor under one key, so a writer who turns
 * transliteration off does not have to turn it off again in every field.
 */

const STORAGE_KEY = 'kaha:transliteration';

export function useTransliterationEnabled(): [boolean, (value: boolean) => void] {
  const [enabled, setEnabled] = useState(() => localStorage.getItem(STORAGE_KEY) !== 'off');

  const update = useCallback((value: boolean) => {
    setEnabled(value);
    localStorage.setItem(STORAGE_KEY, value ? 'on' : 'off');
  }, []);

  // Keep fields in step when the editor toggles it on the same page.
  useEffect(() => {
    const sync = () => setEnabled(localStorage.getItem(STORAGE_KEY) !== 'off');
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  return [enabled, update];
}

type Field = HTMLInputElement | HTMLTextAreaElement;

/**
 * The alternatives picker, and the typing rules that feed it.
 *
 * The rules themselves — space converts and adds the space, backspace takes it
 * back and offers the readings, roman typed onto a converted word rejoins it —
 * live in `@shared/nepali/browser/typing` and are shared with the embeddable
 * widget. What is here is the React half: reading the field, handing the new
 * value to the caller, and putting the caret back afterwards.
 */
function useAlternates(enabled: boolean, onValueChange: (value: string) => void) {
  const [picker, setPicker] = useState<AlternatesState | null>(null);
  const [active, setActive] = useState(0);
  /**
   * Whether this list was asked for.
   *
   * False when it opened itself after an ambiguous word, which is what stops it
   * taking the Enter that submits a form or the digit that belongs in a year.
   */
  const [engaged, setEngaged] = useState(true);
  const last = useRef<Conversion | null>(null);
  const fieldRef = useRef<Field | null>(null);
  /** Every word this field has converted, so any of them stays clickable. */
  const log = useRef(new ConversionLog());

  const close = useCallback(() => setPicker(null), []);

  /**
   * Applies an edit.
   *
   * The caret is restored on the next frame because the value is the caller's
   * state: the field still holds the old text until React has re-rendered, and
   * a selection set before that lands in the wrong place.
   */
  const apply = (field: Field, value: string, caret: number) => {
    onValueChange(value);
    requestAnimationFrame(() => field.setSelectionRange(caret, caret));
  };

  const open = (field: Field, target?: Conversion, { auto = false } = {}) => {
    const conversion = target ?? last.current;
    if (!conversion) return;
    if (!target && !stillValid(field.value, conversion)) return;

    last.current = conversion;

    // Unasked, only for a word that is genuinely ambiguous — see `worthOffering`.
    if (auto && !worthOffering(conversion.roman)) return;

    const candidates = candidatesFor(conversion.roman);
    // One reading is not a choice; opening a list of it would be a promise that
    // there was something to pick.
    if (candidates.length < 2) return;

    fieldRef.current = field;
    setActive(0);
    setEngaged(!auto);
    // Under the word rather than under the field: on a tall textarea those are
    // a long way apart, and the list is about that word.
    setPicker({ roman: conversion.roman, candidates, anchor: caretRect(field, anchorIndex(conversion)) });
  };

  const pick = (index: number) => {
    const field = fieldRef.current;
    const conversion = last.current;
    if (!field || !conversion || !picker) return;

    const chosen = picker.candidates[index < 0 ? active : index];
    if (!chosen) return;

    const caret = conversion.from + chosen.text.length;
    apply(field, field.value.slice(0, conversion.from) + chosen.text + field.value.slice(conversion.to), caret);
    rememberPick(conversion.roman, chosen.text);

    last.current = { ...conversion, text: chosen.text, to: caret };
    log.current.remember(last.current);
    setPicker(null);
    requestAnimationFrame(() => field.focus());
  };

  /** Returns true when the key was the picker's and must not reach the field. */
  const onKeyDown = (event: React.KeyboardEvent<Field>): boolean => {
    const field = event.currentTarget;

    if (picker) {
      const action = alternatesAction(event, picker.candidates.length, { engaged });
      if (action) {
        event.preventDefault();
        if (action.type === 'close') close();
        else if (action.type === 'pick') pick(action.index);
        else {
          // An arrow is the writer reaching for the list; from there it may take
          // Enter and the digits too.
          setEngaged(true);
          setActive((current) => (current + action.delta + picker.candidates.length) % picker.candidates.length);
        }
        return true;
      }
      // Anything else means the writer has moved on and kept what they had. Not
      // prevented: on a list that opened by itself this is the next word.
      close();
    }

    if (!enabled) return false;

    if (isAlternatesTrigger(event)) {
      event.preventDefault();
      // Ctrl+Space on a word not yet converted converts it *and* offers the
      // readings, so the chord is one thing to learn rather than two.
      if (!stillValid(field.value, last.current)) {
        const caret = field.selectionStart ?? field.value.length;
        const edit = commitOnSpace(field.value, caret, last.current);
        if (edit) {
          // Committed without its trailing space: this is a correction, not the
          // end of a word.
          const trimmed = edit.value.slice(0, edit.caret - 1) + edit.value.slice(edit.caret);
          apply(field, trimmed, edit.caret - 1);
          last.current = edit.conversion;
        }
      }
      open(field);
      return true;
    }

    const caret = field.selectionStart ?? field.value.length;
    if (field.selectionStart !== field.selectionEnd) return false;

    if (event.key === ' ') {
      const edit = commitOnSpace(field.value, caret, last.current);
      if (!edit) return false;

      /*
       * The space is inserted here, and the browser's own is stopped.
       *
       * Letting the default through loses it: the conversion sets React state,
       * React re-renders from that state, and the space the browser had already
       * put in the DOM is overwritten by a value that never contained it.
       */
      event.preventDefault();
      apply(field, edit.value, edit.caret);
      last.current = edit.conversion;
      log.current.remember(last.current);
      // The other readings, when there are any — offered rather than waited for.
      // Costs nothing to ignore; see `alternatesAction`.
      requestAnimationFrame(() => open(field, undefined, { auto: true }));
      return true;
    }

    if (event.key === 'Backspace') {
      const edit = backspaceAfterConversion(field.value, caret, last.current);
      if (!edit) return false;

      event.preventDefault();
      apply(field, edit.value, edit.caret);
      last.current = edit.conversion;
      // Deleting that space is somebody saying the word came out wrong.
      requestAnimationFrame(() => open(field));
      return true;
    }

    return false;
  };

  /**
   * Clicking a converted word offers its other readings.
   *
   * The only way to reach the picker on a phone, where there is no Ctrl+Space to
   * press — and the one people find on a desktop without being told. A click on
   * anything this field did not convert is left alone.
   */
  const onClick = (event: React.MouseEvent<Field>) => {
    const field = event.currentTarget;
    if (!enabled || field.selectionStart !== field.selectionEnd) return;

    const found = log.current.find(field.value, field.selectionStart ?? 0);
    if (found) open(field, found);
    else close();
  };

  const onBlur = (event: React.FocusEvent<Field>) => {
    const field = event.currentTarget;
    if (enabled) {
      const edit = commitOnSpace(`${field.value} `, field.value.length + 1, last.current);
      if (edit) {
        const trimmed = edit.value.trimEnd();
        apply(field, trimmed, trimmed.length);
        last.current = edit.conversion;
      }
    }
    close();
  };

  const popup = picker ? (
    <AlternatesPopup state={picker} activeIndex={active} onPick={pick} onClose={close} />
  ) : null;

  return { onKeyDown, onClick, onBlur, popup };
}

interface NepaliInputProps extends Omit<React.ComponentProps<'input'>, 'onChange' | 'value'> {
  value: string;
  onValueChange: (value: string) => void;
  /** Off for handles, emails, URLs — anything that must stay ASCII. */
  enabled?: boolean;
}

export const NepaliInput = forwardRef<HTMLInputElement, NepaliInputProps>(
  (
    {
      value,
      onValueChange,
      enabled = true,
      className,
      // Pulled out of the spread rather than left in it: `{...props}` is applied
      // last, so a caller's own handler would silently replace the one that does
      // the transliteration instead of running alongside it.
      onKeyDown: onKeyDownProp,
      onBlur: onBlurProp,
      ...props
    },
    ref,
  ) => {
    const { onKeyDown, onClick, onBlur, popup } = useAlternates(enabled, onValueChange);

    return (
      <div className="relative">
        <Input
          ref={ref}
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          onKeyDown={(event) => {
            onKeyDown(event);
            onKeyDownProp?.(event);
          }}
          onClick={onClick}
          onBlur={(event) => {
            onBlur(event);
            onBlurProp?.(event);
          }}
          className={cn(enabled && 'np pr-8', className)}
          lang={enabled ? 'ne' : undefined}
          {...props}
        />
        {enabled && (
          <Languages
            className="pointer-events-none absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground/60"
            aria-hidden="true"
          />
        )}
        {popup}
      </div>
    );
  },
);
NepaliInput.displayName = 'NepaliInput';

interface NepaliTextareaProps extends Omit<React.ComponentProps<'textarea'>, 'onChange' | 'value'> {
  value: string;
  onValueChange: (value: string) => void;
  enabled?: boolean;
}

/**
 * The same conversion, for text that runs to more than a line.
 *
 * A reader leaving a comment is typing Nepali on whatever keyboard they have,
 * which for most of them is a Latin one — the same problem the editor solves for
 * writers. Enter is left alone here: in a comment box it submits nothing and
 * ends a line, and converting on it would fight someone breaking a quotation.
 */
export const NepaliTextarea = forwardRef<HTMLTextAreaElement, NepaliTextareaProps>(
  (
    { value, onValueChange, enabled = true, className, onKeyDown: onKeyDownProp, onBlur: onBlurProp, ...props },
    ref,
  ) => {
    const { onKeyDown, onClick, onBlur, popup } = useAlternates(enabled, onValueChange);

    return (
      <div className="relative">
        <Textarea
          ref={ref}
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          onKeyDown={(event) => {
            onKeyDown(event);
            onKeyDownProp?.(event);
          }}
          onClick={onClick}
          onBlur={(event) => {
            onBlur(event);
            onBlurProp?.(event);
          }}
          className={cn(enabled && 'np', className)}
          lang={enabled ? 'ne' : undefined}
          {...props}
        />
        {popup}
      </div>
    );
  },
);
NepaliTextarea.displayName = 'NepaliTextarea';
